/**
 * Animated Logo Effect - Single Logo Approach
 * Transforms the actual header logo from large/centered to small/header position
 */

class AnimatedHeaderLogo {
  constructor() {
    // Target the actual header logo (supports multiple class patterns)
    this.headerLogo = document.querySelector('.header-logo, .header__logo, .header__heading-logo');
    this.header = document.querySelector('.shopify-section-header, header, .header');
    
    if (!this.headerLogo || !this.header) {
      console.warn('AnimatedHeaderLogo: Header or logo not found');
      return;
    }

    // Get settings from data attributes or use defaults
    const logoWrapper = this.headerLogo.closest('[data-scroll-distance]') || document.body;
    this.config = {
      scrollTriggerEnd: parseInt(logoWrapper.dataset.scrollDistance) || 400,
      heroScale: parseFloat(logoWrapper.dataset.heroScale) || 3.0,
      mobileHeroScale: parseFloat(logoWrapper.dataset.mobileHeroScale) || 2.0
    };

    this.isMobile = window.innerWidth < 768;
    this.ticking = false;
    this.isInitialized = false;
    
    this.init();
  }

  init() {
    // Wait for layout to settle
    requestAnimationFrame(() => {
      this.calculateInitialState();
      this.setupStyles();
      this.updateLogoTransform(0);
      this.isInitialized = true;
      
      // Listen to scroll events
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      window.addEventListener('resize', () => this.onResize());
      
      // Mark as ready
      document.body.classList.add('animated-logo-ready');
    });
  }

  calculateInitialState() {
    // Get the logo's natural position in the header
    const logoRect = this.headerLogo.getBoundingClientRect();
    const headerRect = this.header.getBoundingClientRect();
    
    // Calculate where logo should be when large (centered on viewport)
    this.centerY = window.innerHeight * 0.4; // 40% from top
    this.centerX = window.innerWidth / 2;
    
    // Store the final position (where logo naturally sits in header)
    this.finalY = logoRect.top + logoRect.height / 2;
    this.finalX = logoRect.left + logoRect.width / 2;
    
    // Calculate transformation needed
    this.translateYDelta = this.centerY - this.finalY;
    this.translateXDelta = this.centerX - this.finalX;
  }

  setupStyles() {
    // Add strong CSS class to logo for styling
    this.headerLogo.classList.add('animated-header-logo');
    this.header.classList.add('has-animated-logo');
    
    // Apply initial transform origin
    this.headerLogo.style.transformOrigin = 'center center';
  }

  onScroll() {
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateLogoTransform(window.pageYOffset);
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  onResize() {
    this.isMobile = window.innerWidth < 768;
    this.calculateInitialState();
    this.updateLogoTransform(window.pageYOffset);
  }

  updateLogoTransform(scrollY) {
    if (!this.isInitialized) return;
    
    // Calculate scroll progress (1 at top, 0 after scrolling)
    const progress = Math.max(0, Math.min(1, (this.config.scrollTriggerEnd - scrollY) / this.config.scrollTriggerEnd));
    
    // Apply easing
    const easedProgress = this.easeOutCubic(progress);
    
    // Calculate scale (from heroScale to 1)
    const initialScale = this.isMobile ? this.config.mobileHeroScale : this.config.heroScale;
    const scale = 1 + (initialScale - 1) * easedProgress;
    
    // Calculate translation
    const translateY = this.translateYDelta * easedProgress;
    const translateX = this.translateXDelta * easedProgress;
    
    // Apply transform
    this.headerLogo.style.transform = `translate(${translateX}px, ${translateY}px) scale(${scale})`;
    this.headerLogo.style.zIndex = progress > 0.1 ? '1000' : '1';
    
    // Update header state
    if (progress > 0.95) {
      this.header.classList.add('logo-is-hero');
      this.header.classList.remove('logo-is-small');
    } else if (progress < 0.05) {
      this.header.classList.remove('logo-is-hero');
      this.header.classList.add('logo-is-small');
    } else {
      this.header.classList.remove('logo-is-hero', 'logo-is-small');
    }
  }

  easeOutCubic(t) {
    return 1 - Math.pow(1 - t, 3);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    // Only run on homepage
    if (document.body.classList.contains('template-index') || 
        document.body.classList.contains('template--index') ||
        window.location.pathname === '/') {
      new AnimatedHeaderLogo();
    }
  });
} else {
  // Only run on homepage
  if (document.body.classList.contains('template-index') || 
      document.body.classList.contains('template--index') ||
      window.location.pathname === '/') {
    new AnimatedHeaderLogo();
  }
}

